import React from "react";

const SaveIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
    <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
  </svg>
);

const ZapIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

import { useQueryClient } from "@tanstack/react-query";
import {
  getAIProvider,
  setAIProvider,
  getOpenAIKey,
  setOpenAIKey,
  getOpenRouterKey,
  setOpenRouterKey,
  getAIModel,
  setAIModel,
  getAIFastModel,
  setAIFastModel,
  getAIVisionModel,
  setAIVisionModel,
  clearAIData,
  getAIExcludedLocations,
  setAIExcludedLocations,
  getAIExcludedProducts,
  setAIExcludedProducts,
  type AIProvider,
} from "utils/storage.ts";
import ProductPicker from "components/ProductPicker.tsx";
import SettingsSection from "./components/SettingsSection.tsx";
import SettingsRow from "./components/SettingsRow.tsx";
import SettingsActions from "./components/SettingsActions.tsx";
import SettingButton from "./components/SettingButton.tsx";
import ModelPicker from "./components/ModelPicker.tsx";
import { useOpenAIModelsQuery, useOpenRouterModelsQuery, useLocationsQuery, useProductsQuery } from "hooks/useQueries.ts";
import { ModelOption } from "./types.ts";
type OpenAIModel = { id: string };
type OpenAIModelsResponse = { data: OpenAIModel[] };
type OpenRouterModel = { id: string; name?: string };
type OpenRouterModelsResponse = { data: OpenRouterModel[] };

const AISettings: React.FC = () => {
  const queryClient = useQueryClient();

  const [aiProvider, setProviderState] = React.useState<AIProvider>(getAIProvider());
  const [openaiKey, setOpenAIKeyState] = React.useState<string>(getOpenAIKey() ?? "");
  const [openrouterKey, setOpenRouterKeyState] = React.useState<string>(getOpenRouterKey() ?? "");
  const [aiModel, setAIModelState] = React.useState<string>(getAIModel() ?? "");
  const [aiFastModel, setAIFastModelState] = React.useState<string>(getAIFastModel() ?? "");
  const [aiVisionModel, setAIVisionModelState] = React.useState<string>(getAIVisionModel() ?? "");
  // Removed X-Title; keep UI minimal
  const [modelSearch, setModelSearch] = React.useState("");
  const [productSearch, setProductSearch] = React.useState("");
  const [fastModelSearch, setFastModelSearch] = React.useState("");
  const [visionModelSearch, setVisionModelSearch] = React.useState("");

  // Excluded locations/products state
  const [excludedLocationsState, setExcludedLocationsState] = React.useState<Set<number>>(() => getAIExcludedLocations());
  const [excludedProductsState, setExcludedProductsState] = React.useState<Set<number>>(() => getAIExcludedProducts());

  const { data: openaiModels } = useOpenAIModelsQuery(aiProvider === "openai" ? openaiKey : null);
  const { data: openrouterModels } = useOpenRouterModelsQuery();
  const { data: locations = [] } = useLocationsQuery();
  const { data: products = [] } = useProductsQuery();

  const productsMap = React.useMemo(() => {
    const m = new Map<number, { id: number; name: string }>();
    (products as Array<{ id: number; name: string }>).forEach(p => m.set(p.id, p));
    return m;
  }, [products]);

  const modelOptions: ModelOption[] = React.useMemo(() => {
    const openai = openaiModels as OpenAIModelsResponse | undefined;
    const openrouter = openrouterModels as OpenRouterModelsResponse | undefined;
    if (aiProvider === "openai" && openai?.data) {
      return openai.data.map((m: OpenAIModel) => ({ id: m.id, label: m.id }));
    }
    if (aiProvider === "openrouter" && openrouter?.data) {
      return openrouter.data.map((m: OpenRouterModel) => ({ id: m.id, label: m.name ?? m.id }));
    }
    return [];
  }, [aiProvider, openaiModels, openrouterModels]);

  const handleSaveAI = () => {
    setAIProvider(aiProvider);
    setOpenAIKey(aiProvider === "openai" ? (openaiKey || null) : getOpenAIKey());
    setOpenRouterKey(aiProvider === "openrouter" ? (openrouterKey || null) : getOpenRouterKey());
    setAIModel(aiModel || null);
    setAIFastModel(aiFastModel || null);
    setAIVisionModel(aiVisionModel || null);
    // Persist filters
    setAIExcludedLocations(excludedLocationsState);
    setAIExcludedProducts(excludedProductsState);
    // No X-Title persistence
    queryClient.invalidateQueries({ queryKey: ["ai-models-openai"] });
    queryClient.invalidateQueries({ queryKey: ["ai-models-openrouter"] });
    // Clear AI suggestions cache when filtering changes
    queryClient.removeQueries({ queryKey: ["ai-suggestions"] });
    
    // Update local state to match what was actually saved
    // This ensures hasChanges becomes false after saving
    setProviderState(aiProvider);
    setOpenAIKeyState(aiProvider === "openai" ? (openaiKey || "") : "");
    setOpenRouterKeyState(aiProvider === "openrouter" ? (openrouterKey || "") : "");
    setAIModelState(aiModel || "");
    setAIFastModelState(aiFastModel || "");
    setAIVisionModelState(aiVisionModel || "");
    setExcludedLocationsState(new Set(excludedLocationsState));
    setExcludedProductsState(new Set(excludedProductsState));
    
    alert("AI settings saved.");
  };

  const handleClearAI = () => {
    clearAIData();
    setProviderState(null);
    setOpenAIKeyState("");
    setOpenRouterKeyState("");
    setAIModelState("");
    setAIFastModelState("");
    setAIVisionModelState("");
    setExcludedLocationsState(new Set());
    setExcludedProductsState(new Set());
    // No X-Title state
    queryClient.invalidateQueries({ queryKey: ["ai-models-openai"] });
    queryClient.invalidateQueries({ queryKey: ["ai-models-openrouter"] });
    alert("AI settings cleared.");
  };

  const handleLocationToggle = (locationId: number) => {
    setExcludedLocationsState(prev => {
      const newSet = new Set(prev);
      if (newSet.has(locationId)) {
        newSet.delete(locationId);
      } else {
        newSet.add(locationId);
      }
      return newSet;
    });
  };

  const handleProductAdd = (productId: number) => {
    setExcludedProductsState(prev => {
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
  };

  const handleProductRemove = (productId: number) => {
    setExcludedProductsState(prev => {
      const next = new Set(prev);
      next.delete(productId);
      return next;
    });
  };

  const setsEqual = (a: Set<number>, b: Set<number>) => {
    if (a.size !== b.size) return false;
    for (const v of a) if (!b.has(v)) return false;
    return true;
  };

  const hasChanges = React.useMemo(() => {
    const provChanged = getAIProvider() !== aiProvider;
    const openaiChanged = (getOpenAIKey() ?? "") !== openaiKey;
    const openrouterChanged = (getOpenRouterKey() ?? "") !== openrouterKey;
    const modelChanged = (getAIModel() ?? "") !== aiModel;
    const fastModelChanged = (getAIFastModel() ?? "") !== aiFastModel;
    const visionModelChanged = (getAIVisionModel() ?? "") !== aiVisionModel;
    const locChanged = !setsEqual(getAIExcludedLocations(), excludedLocationsState);
    const prodChanged = !setsEqual(getAIExcludedProducts(), excludedProductsState);
    return provChanged || openaiChanged || openrouterChanged || modelChanged || fastModelChanged || visionModelChanged || locChanged || prodChanged;
  }, [aiProvider, openaiKey, openrouterKey, aiModel, aiFastModel, aiVisionModel, excludedLocationsState, excludedProductsState]);



  const handleTestAI = async () => {
    try {
      if (aiProvider === "openai") {
        if (!openaiKey) throw new Error("OpenAI key required");
        const res = await fetch("https://api.openai.com/v1/models", {
          headers: { Authorization: `Bearer ${openaiKey}` },
        });
        if (!res.ok) throw new Error(`OpenAI test failed: ${res.status}`);
        alert("OpenAI connection OK");
        return;
      }
      if (aiProvider === "openrouter") {
        if (!openrouterKey) throw new Error("OpenRouter key required for test");
        const model = aiModel || "openai/gpt-4o-mini-2024-07-18";
        const res = await fetch("https://openrouter.ai/api/v1/chat/completions", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${openrouterKey}`,
          },
          body: JSON.stringify({
            model,
            messages: [{ role: "user", content: "ping" }],
            max_tokens: 16,
          }),
        });
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(`OpenRouter test failed: ${res.status}${text ? ` ${text}` : ""}`);
        }
        alert("OpenRouter connection OK");
        return;
      }
      alert("Select a provider first.");
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      alert(message);
    }
  };

  return (
    <>
      <SettingsSection title="AI Connection">
        <SettingsRow label="Provider" labelAbove fullWidth>
          <select
            className="settings-page__input"
            value={aiProvider ?? ''}
            onChange={(e) => setProviderState((e.target.value || null) as AIProvider)}
          >
            <option value="">Select…</option>
            <option value="openai">OpenAI</option>
            <option value="openrouter">OpenRouter</option>
          </select>
        </SettingsRow>

        <SettingsRow label="API Key" labelAbove fullWidth>
          {aiProvider === 'openai' && (
            <input
              type="password"
              name="openai_api_key"
              placeholder="sk-..."
              value={openaiKey}
              onChange={(e) => setOpenAIKeyState(e.target.value)}
              className="settings-page__input"
              autoComplete="new-password"
              inputMode="text"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
            />
          )}
          {aiProvider === 'openrouter' && (
            <input
              type="password"
              name="openrouter_api_key"
              placeholder="sk-or-v1-..."
              value={openrouterKey}
              onChange={(e) => setOpenRouterKeyState(e.target.value)}
              className="settings-page__input"
              autoComplete="new-password"
              inputMode="text"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
            />
          )}
        </SettingsRow>

        <SettingsRow label="Get a key:">
          {aiProvider === 'openai' && (
            <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer">Get an OpenAI API key</a>
          )}
          {aiProvider === 'openrouter' && (
            <a href="https://openrouter.ai/keys" target="_blank" rel="noreferrer">Get an OpenRouter API key</a>
          )}
          {!aiProvider && (
            <>
              <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer">OpenAI</a> · <a href="https://openrouter.ai/keys" target="_blank" rel="noreferrer">OpenRouter</a>
            </>
          )}
        </SettingsRow>

        <SettingsRow label="Model" labelAbove fullWidth>
          <ModelPicker
            label="Model"
            modelOptions={modelOptions}
            selectedModel={aiModel}
            searchTerm={modelSearch}
            onModelSelect={(modelId) => {
              setAIModelState(modelId);
              setModelSearch("");
            }}
            onSearchTermChange={setModelSearch}
            placeholder="Type to search models..."
          />
        </SettingsRow>

        <SettingsRow label="Fast model" labelAbove fullWidth>
          <ModelPicker
            label="Fast model"
            modelOptions={modelOptions}
            selectedModel={aiFastModel}
            searchTerm={fastModelSearch}
            onModelSelect={(modelId) => {
              setAIFastModelState(modelId);
              setFastModelSearch("");
            }}
            onSearchTermChange={setFastModelSearch}
            placeholder="Type to search fast models..."
          />
        </SettingsRow>

        <SettingsRow label="Expiry photo model" labelAbove fullWidth>
          <ModelPicker
            label="Expiry photo model"
            modelOptions={modelOptions}
            selectedModel={aiVisionModel}
            searchTerm={visionModelSearch}
            onModelSelect={(modelId) => {
              setAIVisionModelState(modelId);
              setVisionModelSearch("");
            }}
            onSearchTermChange={setVisionModelSearch}
            placeholder="Type to search image-capable models..."
          />
        </SettingsRow>

        <SettingsActions>
           <SettingButton
             onClick={handleSaveAI}
             variant="primary"
             icon={<SaveIcon />}
             disabled={!hasChanges}
           >
             Save
           </SettingButton>
           <SettingButton onClick={handleTestAI} variant="quiet" icon={<ZapIcon />}>
             Test Connection
           </SettingButton>
           <SettingButton onClick={handleClearAI} variant="danger" icon={<TrashIcon />}>
             Clear AI Data
           </SettingButton>
        </SettingsActions>
      </SettingsSection>

      <SettingsSection title="AI Pantry Filters">
        <div className="settings-page__ai-note">
          Choose which pantry locations the AI should use, and which individual products it should ignore when generating suggestions.
        </div>

        <SettingsRow label="Use locations:" fullWidth labelAbove>
          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {(locations as Array<{ id: number; name: string }>).map(location => (
                <label
                  key={location.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: excludedLocationsState.has(location.id) ? '#f5f5f5' : '#fff',
                    color: excludedLocationsState.has(location.id) ? '#999' : '#333',
                    fontSize: '14px',
                    userSelect: 'none'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!excludedLocationsState.has(location.id)}
                    onChange={() => handleLocationToggle(location.id)}
                    style={{ marginRight: '6px' }}
                  />
                  {location.name}
                </label>
              ))}
            </div>
          </div>
        </SettingsRow>

        <SettingsRow label="Exclude products:" fullWidth labelAbove>
          <div style={{ width: '100%' }}>
            <div style={{ marginBottom: 8 }}>
              <ProductPicker
                searchTerm={productSearch}
                onSearchTermChange={setProductSearch}
                onSelect={(p: { id: number }) => { handleProductAdd(p.id); setProductSearch(""); }}
                showCreateNew={false}
                showList={true}
              />
            </div>

            {excludedProductsState.size > 0 && (
              <div className="settings-page__excluded-list">
                {Array.from(excludedProductsState).map(pid => (
                  <span key={pid} className="settings-page__excluded-item">
                    <span className="settings-page__excluded-name">{productsMap.get(pid)?.name ?? `#${pid}`}</span>
                    <button
                      type="button"
                      className="settings-page__excluded-remove"
                      onClick={() => handleProductRemove(pid)}
                      aria-label="Remove excluded product"
                      title="Remove"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </SettingsRow>
      </SettingsSection>
    </>
  );
};

export default AISettings;
